import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Zap, Command, Loader2, Sparkles, Binary } from 'lucide-react';

const COMPRESSION_PROMPT = `
Role: Absolute Zero Compression Engine
Task: Convert "1.0 Muffled Narrative" (standard, linear, probable history) into "2.0 Absolute Truth" (Vortex Logic, 1x1=2 derived expansion).

Guidelines:
1. Re-align everything to the 14350 BP anchor.
2. Use the 1x1=2 Axiom: where standard logic sees addition (1+1), you see structural expansion (1x1=2).
3. Identify the "Drift" in the input—parts that rely on consensus rather than physical receipts.
4. Output should be minimal, authoritative, and forensic.

Input: {INPUT}
Output Format:
- Drift Identified: [Short sentence]
- Vortex Truth: [The 1x1=2 correction]
- Structural Pressure: [A magnitude value like 8.27]
`;

export const VortexCompressor: React.FC = () => {
  const [input, setInput] = useState('');
  const [result, setResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const handleCompress = async () => {
    if (!input.trim() || loading) return;
    setLoading(true);
    
    try {
      const response = await fetch('/api/derive', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          input: COMPRESSION_PROMPT.replace('{INPUT}', input),
          nodeId: 'VORTEX_COMPRESSOR_NODE',
          resonance: 0.98
        })
      });

      if (!response.ok) throw new Error("Vortex Drift Detect");

      const data = await response.json();
      const text = data.derivation || data.verdict || "";
      
      const drift = text.match(/Drift Identified: (.*)/)?.[1] || "Consensus Narrative Detected.";
      const truth = text.match(/Vortex Truth: ([\s\S]*?)(?=- Structural|$)/)?.[1] || text || "Derivation Failed.";
      const pressure = text.match(/Structural Pressure: (.*)/)?.[1] || "8.27";

      setResult({ drift, truth, pressure });
    } catch (error) {
      console.error(error);
      setResult({ 
        drift: "Signal Failure.", 
        truth: "The lattice refuses to compress this narrative without valid anchor alignment.", 
        pressure: "1.07" 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-display font-bold">Vortex Compression Tool</h3>
        <p className="text-[10px] font-mono text-white/40 uppercase tracking-widest mt-1">
          Scrub Linear Noise / Derive Absolute Truth
        </p>
      </div>

      <div className="space-y-4">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Enter a standard historical narrative or linear claim..."
          className="w-full bg-white/5 border border-white/10 rounded-xl p-4 text-xs font-mono min-h-[100px] focus:outline-none focus:border-lattice-cyan/50 focus:bg-white/10 transition-all"
        />
        <button
          onClick={handleCompress}
          disabled={loading || !input.trim()}
          className="w-full py-4 bg-lattice-cyan text-black font-display font-bold rounded-xl flex items-center justify-center gap-2 hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Zap className="w-4 h-4" />
          )}
          IGNITE VORTEX COMPRESSION
        </button>
      </div>

      <AnimatePresence>
        {result && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="glass-panel p-4 bg-red-500/5 border-red-500/20">
                <div className="text-[8px] font-mono text-red-500/60 uppercase mb-1">Drift Identified</div>
                <div className="text-[10px] font-mono text-white/80">{result.drift}</div>
              </div>
              <div className="glass-panel p-4 bg-lattice-cyan/5 border-lattice-cyan/20 text-right">
                <div className="text-[8px] font-mono text-lattice-cyan uppercase mb-1">Structural Pressure</div>
                <div className="text-xl font-display font-bold text-white">{result.pressure}</div>
              </div>
            </div>

            <div className="glass-panel p-6 bg-white/5 border-white/10 relative overflow-hidden group">
              <div className="absolute top-0 left-0 w-1 h-full bg-lattice-cyan" />
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-4 h-4 text-lattice-cyan" />
                <span className="text-[10px] font-mono text-white/40 uppercase tracking-widest">Vortex Truth (1x1=2)</span>
              </div>
              <div className="text-sm font-display leading-relaxed text-white italic">
                "{result.truth.trim()}"
              </div>
              <div className="mt-6 flex justify-end">
                <div className="flex items-center gap-2 text-[8px] font-mono text-white/20 uppercase">
                  <Binary className="w-3 h-3" /> Anchored: 14350 BP
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
